import { useState } from "react";
import axiosInstance from "../../../api/Axiosinstance";
import { ENDPOINTS } from "../../../api/Endpoints";
import { useToast } from "../../../hooks/useToast";
import { getApiErrorMessage } from "../../../utils/apiError";
import providersService from "../../../api/services/providersService";
import { authStore } from "../../../stores/auth.store";

const apiRoot =
  (process.env.REACT_APP_API_URL || "").replace(/\/api$/, "") ||
  "https://pulo-staging.onrender.com";

const parseErrorMessage = (err, defaultMessage = "Login failed. Please try again.") => {
  // Rate limiting is the one case the generic extractor can't infer from the
  // body alone — it lives in the status code.
  if (err.response?.status === 429) {
    return (
      err.response?.data?.message ||
      "Too many login attempts. Please wait a few minutes and try again."
    );
  }

  return getApiErrorMessage(err, defaultMessage);
};

export const useSignin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  const extractAccessToken = (payload) =>
    payload?.accessToken ||
    payload?.token ||
    payload?.data?.accessToken ||
    payload?.data?.token ||
    payload?.access_token ||
    payload?.data?.access_token ||
    null;

  const unwrapResponse = (response) => {
    if (!response) return {};
    return (
      response.data?.data?.profile ||
      response.data?.data?.user ||
      response.data?.data ||
      response.data?.profile ||
      response.data ||
      response.profile ||
      response
    );
  };

  const normalizeList = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === "string") {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    if (value && typeof value === "object") {
      return Object.values(value).flat().filter(Boolean);
    }
    return [];
  };

  const getVerificationRedirect = (email) =>
    `/auth/verify-email?email=${encodeURIComponent(email || "")}`;

  const getEmailVerificationStatus = (user, payload = {}) => {
    let hasTrue = false;
    let hasFalse = false;

    const recordBoolean = (value) => {
      if (typeof value === "boolean") {
        if (value) hasTrue = true;
        else hasFalse = true;
        return;
      }
      if (typeof value === "number") {
        if (value === 1) hasTrue = true;
        if (value === 0) hasFalse = true;
        return;
      }
      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (!normalized) return;
        if (normalized === "true" || normalized === "1") hasTrue = true;
        if (normalized === "false" || normalized === "0") hasFalse = true;
      }
    };

    const recordTimestamp = (value) => {
      if (value === undefined) return;
      if (value === null) {
        hasFalse = true;
        return;
      }
      if (typeof value === "number") {
        if (value > 0) hasTrue = true;
        else hasFalse = true;
        return;
      }
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed) hasTrue = true;
        else hasFalse = true;
        return;
      }
      if (value) hasTrue = true;
      else hasFalse = true;
    };

    const recordStatus = (value) => {
      if (typeof value !== "string") return;
      const normalized = value.trim().toLowerCase();
      if (!normalized) return;
      const truthy = ["verified", "email_verified", "confirmed"];
      const falsy = [
        "unverified",
        "not_verified",
        "not verified",
        "pending",
        "unconfirmed",
        "email_not_verified",
        "email_unverified",
      ];
      if (truthy.includes(normalized)) hasTrue = true;
      if (falsy.includes(normalized)) hasFalse = true;
    };

    [user, payload].forEach((source) => {
      if (!source || typeof source !== "object") return;
      recordBoolean(source.isEmailVerified);
      recordBoolean(source.emailVerified);
      recordBoolean(source.email_verified);
      recordBoolean(source.is_verified);
      recordBoolean(source.isVerified);
      recordBoolean(source.verified);
      recordTimestamp(source.emailVerifiedAt);
      recordTimestamp(source.email_verified_at);
      recordTimestamp(source.verifiedAt);
      recordStatus(source.emailVerificationStatus);
      recordStatus(source.verificationStatus);
    });

    if (hasTrue) return true;
    if (hasFalse) return false;
    return null;
  };

  const isAdminRole = (role) => {
    if (!role) return false;
    const normalized = String(role).trim().toLowerCase();
    if (normalized.includes("admin")) return true;
    return ["super_admin", "superadmin"].includes(normalized);
  };

  const isVerificationError = (err) => {
    const message = String(
      err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        ""
    )
      .trim()
      .toLowerCase();
    const code = String(
      err.response?.data?.code ||
        err.response?.data?.errorCode ||
        err.response?.data?.status ||
        ""
    )
      .trim()
      .toLowerCase();

    if (code && /(verify|verification|unverified|not_verified|email_not_verified)/.test(code)) {
      return true;
    }
    if (message && /email/.test(message) && /(verify|verification|unverified|not verified)/.test(message)) {
      return true;
    }
    if (message.includes("verify your email")) return true;

    return false;
  };

  const getAssistantOnboardingRedirect = async () => {
    const [profileResult, expertiseResult] = await Promise.allSettled([
      providersService.getProfile(),
      providersService.getExpertise(),
    ]);

    const profile = unwrapResponse(
      profileResult.status === "fulfilled" ? profileResult.value : null
    );
    const expertise = unwrapResponse(
      expertiseResult.status === "fulfilled" ? expertiseResult.value : null
    );

    const completion = profile?.completion || {};
    const hasCompletionFlags =
      completion && typeof completion === "object" && Object.keys(completion).length > 0;

    const hasBio = Boolean(profile?.bio?.trim());
    const hasServices =
      Array.isArray(profile?.services) && profile.services.length > 0;
    const profilePicture =
      profile?.profilePicture?.url ||
      (typeof profile?.profilePicture === "string" ? profile.profilePicture : "") ||
      profile?.photo ||
      profile?.image ||
      "";
    const hasKyc = hasCompletionFlags
      ? Boolean(completion.personalInformation)
      : hasBio && hasServices && Boolean(profilePicture);

    const experienceYears =
      expertise?.experienceYears ?? profile?.experienceYears ?? 0;
    const languages = normalizeList(
      expertise?.languages || profile?.languages
    );
    const specializations = normalizeList(
      expertise?.specializations || profile?.specializations
    );
    const hasExperience = hasCompletionFlags
      ? Boolean(completion.experience)
      : Number(experienceYears) > 0 &&
        languages.length > 0 &&
        specializations.length > 0;

    const licenseSource =
      profile?.license ||
      profile?.licenseUrl ||
      profile?.license_url ||
      profile?.licenseDocument ||
      profile?.documents?.license ||
      profile?.documents?.licenseUrl;
    const hasLicense = hasCompletionFlags
      ? Boolean(completion.documentVerification)
      : Boolean(
          typeof licenseSource === "string"
            ? licenseSource.trim()
            : licenseSource?.url ||
                licenseSource?.fileUrl ||
                licenseSource?.path ||
                licenseSource?.filename
        );

    const vehicle =
      profile?.vehicle || profile?.vehicleInfo || profile?.vehicleDetails || {};
    const hasVehicle = hasCompletionFlags
      ? Boolean(completion.vehicleInformation)
      : Boolean(
          vehicle?.type ||
            vehicle?.brand ||
            vehicle?.plateNumber ||
            vehicle?.plate_number ||
            vehicle?.color ||
            (Array.isArray(vehicle?.images) && vehicle.images.length > 0)
        );

    const pricingAmount =
      profile?.pricePerDay ??
      profile?.price_per_day ??
      profile?.pricing?.pricePerDay ??
      profile?.pricing?.price_per_day;
    const hasPricing = hasCompletionFlags
      ? Boolean(completion.pricingAndAccount)
      : Number(pricingAmount) > 0 ||
        Boolean(
          profile?.payment?.accountNumber ||
            profile?.payment?.account_number ||
            profile?.bank?.accountNumber ||
            profile?.bank?.account_number
        );

    const steps = [
      { done: hasKyc, path: "/assistant/kyc" },
      { done: hasExperience, path: "/assistant/experience" },
      { done: hasLicense, path: "/assistant/documents" },
      { done: hasVehicle, path: "/assistant/vehicle" },
      { done: hasPricing, path: "/assistant/pricing" },
    ];

    const firstIncomplete = steps.find((step) => !step.done);
    return {
      redirectTo: firstIncomplete
        ? firstIncomplete.path
        : "/dashboards/assistant/overview",
      isComplete: !firstIncomplete,
    };
  };

  const signin = async (email, password, rememberMe = false) => {
    const normalizedEmail = (email || "").trim().toLowerCase();
    try {
      setLoading(true);
      setError(null);

      // Make API call using axios instance
      const response = await axiosInstance.post(ENDPOINTS.AUTH.LOGIN, {
        email,
        password,
        rememberMe,
      });

      // Handle successful login
      const data = response.data?.data || {};
      const { accessToken, user } = data;

      const verificationStatus = getEmailVerificationStatus(user, data);
      if (verificationStatus === false) {
        return {
          success: false,
          needsVerification: true,
          redirectTo: getVerificationRedirect(normalizedEmail),
        };
      }

      if (!accessToken) {
        throw new Error("No access token returned from server");
      }

      // Store token via authStore (handles localStorage/sessionStorage with role-scoped key)
      const role = user?.role;
      authStore.setToken(accessToken, rememberMe, role);

      let isProfileComplete = Boolean(
        user?.profileCompleted ||
          user?.profileComplete ||
          user?.isProfileComplete ||
          user?.isOnboarded ||
          ((user?.firstName || user?.lastName || user?.fullName) && (user?.phone || user?.phoneNumber))
      );
      let assistantRedirect = null;

      if (role === "serviceProvider" || role === "assistant") {
        try {
          const onboarding = await getAssistantOnboardingRedirect();
          assistantRedirect = onboarding.redirectTo;
          isProfileComplete = onboarding.isComplete;
        } catch (err) {
          // Ignore profile lookup failures; fallback to base completion logic.
        }
      }

      const storedUser = {
        ...user,
        profileCompleted: isProfileComplete,
        rememberMe,
      };

      authStore.setUser(storedUser);

      const redirectTo = isAdminRole(role)
        ? "/admin"
        : role === "serviceProvider" || role === "assistant"
          ? assistantRedirect ||
            (isProfileComplete
              ? "/dashboards/assistant/overview"
              : "/assistant/kyc")
          : "/user/travel-assistant";

      return { success: true, data: response.data, redirectTo };
    } catch (err) {
      if (isVerificationError(err)) {
        return {
          success: false,
          needsVerification: true,
          redirectTo: getVerificationRedirect(normalizedEmail),
        };
      }

      const errorMessage = parseErrorMessage(err, "Login failed. Please try again.");

      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const adminSignin = async (email, password, rememberMe = false) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.post(ENDPOINTS.AUTH.ADMIN_LOGIN, {
        email: (email || "").trim().toLowerCase(),
        password,
        rememberMe,
      });

      const payload = response.data || {};
      const data = payload.data || payload;
      const accessToken = extractAccessToken(payload) || extractAccessToken(data);
      const user = data.user || data.admin || data.profile || data;

      if (accessToken) {
        authStore.setToken(accessToken, rememberMe, user?.role || 'admin');
      }

      const storedUser = {
        ...user,
        role: user?.role || "admin",
        rememberMe,
      };

      authStore.setUser(storedUser);

      return { success: true, data: response.data, redirectTo: "/admin" };
    } catch (err) {
      const errorMessage = parseErrorMessage(err, "Admin login failed. Please try again.");
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const googleSignin = async () => {
    try {
      setLoading(true);
      setError(null);

      // Handle Google OAuth flow
      window.location.href = `${apiRoot}/auth/google`;
    } catch (err) {
      setError("Google login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const appleSignin = async () => {
    try {
      setLoading(true);
      setError(null);
      window.location.href = `${apiRoot}/auth/apple`;
    } catch (err) {
      setError("Apple login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    signin,
    adminSignin,
    googleSignin,
    appleSignin,
    // Exposed so the OAuth callback lands an assistant on the same step the
    // password flow would, instead of duplicating the onboarding rules.
    getAssistantOnboardingRedirect,
    loading,
    error,
    clearError,
  };
};
